import type { TCabinCategorySubMeta } from '@/helpers/api/graphql/queries/cabinCategoriesAvailability';
import type { AppDispatch, AppGetState } from '@/store';

import { selectSailingData } from '@/ducks/voyageInfo/sailing/selectors';
import { trackVWOGoal, VWOFlag } from '@/helpers/vwo/trackVWOGoal';
import {
  selectCabinByCode,
  selectCabinCategories,
} from '@/reducers/voyagePlanner/chooseCabin/cabinCategories/selectors';
import { VWOGoal } from '@/types/vwo';

import constants from '../../constants';
import { trackButtonClick, trackIconClick, trackLinkClick, trackPopupState } from './common';
import {
  measuringDetailsClick,
  measuringDetailsImpressions,
  measuringPromotionClick,
} from './ecommerce-promotions/measures';

/**
 * ----- Choose cabin tracking -----
 * @link https://virginvoyages.atlassian.net/wiki/spaces/DA/pages/3945365505/VV+-+15th+Sprint+-+Choose+Cabin+Page+Tracking
 */

/**
 * 2. Measuring Promotion Clicks
 * 9. Choose Cabin Page – Meta Category Selection – ‘Show Options’ Button Clicks
 */
export const onSelectMetaCabinAction = (cabinCode: string) => (dispatch: AppDispatch, getState: AppGetState) => {
  const state = getState();
  const selectedCabin = selectCabinCategories(state)?.find(({ code }) => code === cabinCode);
  if (selectedCabin == null) {
    console.error('Must not happen. Clicked cabin not found for tracking');
    return;
  }

  dispatch(measuringPromotionClick([selectedCabin]));

  trackVWOGoal(VWOFlag.CHOOSE_CABIN_30, VWOGoal.MOVE_FROM_META);

  trackButtonClick({
    labels: ['show-options', cabinCode],
  });
};

/**
 * 8. Choose Cabin Page – Meta Category Selection – Expand Image Gallery
 */
export const onExpandImageGallery = (cabinName: string) => {
  trackIconClick({
    labels: ['expand-cabin-image', cabinName],
  });
};

/**
 * 11. Choose Cabin Page – Category Selection – ‘All Cabin Types’ Link Clicks
 */
export const onReturnToMetaChooseCabin = () => {
  trackLinkClick({
    labels: ['all-cabin-types'],
  });
};

/**
 * 14. Choose Cabin Page – Category Selection – Uplift Info Icon Clicks
 */
export const onUpliftInfoIconClick = (cabinName: string) => {
  trackIconClick({
    labels: [constants.EVENT_LABELS.UPLIFT_INFO, cabinName],
  });
};

/**
 * 4. Measuring Product Add to Carts
 * 15. Choose Cabin Page – Category Selection – ‘Book This’ CTA Button Clicks
 */
export const onBookThisAction =
  (metaCode: string, cabinTypeCode: string, isUpsellApplied?: boolean) =>
  (dispatch: AppDispatch, getState: AppGetState) => {
    const state = getState();
    const selectedCabinType = selectCabinByCode(state, cabinTypeCode);
    if (selectedCabinType == null) {
      console.error('Must not happen. Clicked cabin not found for tracking');
      return;
    }

    dispatch(
      measuringDetailsClick({
        isUpsellApplied,
        labels: [constants.EVENT_LABELS.CHOOSE_CABIN, metaCode],
        list: constants.LISTS.YOUR_VOYAGE,
        sailing: selectSailingData(state),
        subMetas: [selectedCabinType],
      }),
    );

    trackVWOGoal(VWOFlag.CHOOSE_CABIN_30, VWOGoal.MOVE_FROM_SUBMETA);

    trackButtonClick({
      labels: [constants.EVENT_LABELS.BOOK_THIS, selectedCabinType.name],
    });
  };

/**
 * 16. Choose Cabin Page – Category Selection – Arrow Clicks
 */
export const onCabinCategoryNavigation = (position: 'left' | 'right', nextCategory: string) => {
  trackButtonClick({
    labels: [position, nextCategory],
  });
};

export const onUpsellChosen = (isChosen: boolean) => {
  trackButtonClick({
    // TODO: add to constants
    labels: [isChosen ? 'yes-please' : 'not-this-time'],
  });
};

export const onUpsellDisplayedAction =
  (upsellSubmeta: TCabinCategorySubMeta) => (dispatch: AppDispatch, getState: AppGetState) => {
    const state = getState();

    trackPopupState({
      isOpen: true,
      labels: [constants.EVENT_LABELS.UPSELL_LIGHTBOX],
    });
    dispatch(
      measuringDetailsImpressions({
        isUpsellEnabled: true,
        list: constants.LISTS.YOUR_VOYAGE,
        sailing: selectSailingData(state),
        subMetaCategory: upsellSubmeta,
      }),
    );
  };
